import {
  Box,
  Typography,
  Button,
  List,
  Card,
  CardContent,
  Stack,
  Avatar,
  Chip,
  Divider,
  useTheme,
} from "@mui/material";
import { useAppDispatch } from "../../hooks/hooks";
import { setCurrentMooloanView } from "../../redux/uiSlice";
import CowIcon from "../CowIcon";
import { Loan } from "../../types/loanTypes";
import EmptyState from "./EmptyState";

const PersonalLoansView: React.FC<{ loans: Loan[]; isMobile: boolean }> = ({
  loans,
  isMobile,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const handleRequestLoan = () => {
    dispatch(setCurrentMooloanView("new-loan"));
  };

  const handleRepayment = (loanId: string) => {
    dispatch(setCurrentMooloanView("repayment"));
    // TODO: Load specific loan data
  };

  const scrollableBoxStyles = {
    maxHeight: isMobile ? "76vh" : "68vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    p: isMobile ? 1 : 2,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          p: isMobile ? 1 : 2,
        }}
      >
        <Typography variant="h6">My Cattle Loans</Typography>
        <Button
          variant="contained"
          size={isMobile ? "small" : "medium"}
          onClick={handleRequestLoan}
          startIcon={<CowIcon fill={theme.palette.common.white} />}
        >
          New Loan
        </Button>
      </Box>

      {loans.length === 0 ? (
        <EmptyState
          icon={<CowIcon fill={theme.palette.text.secondary} size={48} />}
          title="No Active Loans"
          description="Use your cattle as collateral to get a loan for farming inputs"
          action={
            <Button variant="contained" onClick={handleRequestLoan}>
              Apply for Loan
            </Button>
          }
        />
      ) : (
        <List
          sx={{
            width: "100%",
            bgcolor: "background.paper",
          }}
        >
          {loans.map((loan: Loan) => (
            <Card key={loan.id} sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "secondary.main" }}>
                    <CowIcon fill="#fff" />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">
                      {loan.cattleDetails.breed} #{loan.cattleDetails.tagNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {loan.amount} {loan.currency} • {loan.interestRate}% APR
                    </Typography>
                  </Box>
                  <Chip
                    label={loan.status}
                    size="small"
                    color={
                      loan.status === "active"
                        ? "success"
                        : loan.status === "defaulted"
                        ? "error"
                        : "warning"
                    }
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="caption">Next Payment</Typography>
                    <Typography>{loan.nextPayment}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption">Amount Due</Typography>
                    <Typography>
                      {Math.round(loan.amount / loan.term)} {loan.currency}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleRepayment(loan.id)}
                >
                  Make Payment
                </Button>
              </CardContent>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );
};

export default PersonalLoansView;
